// src/api/templates.ts
import { useUserStore } from '@/store/userStore';
import { handleAxiosError } from './errorHandler';
import api from './axiosBase';
import axios from 'axios';

export interface Version {
  subject: string;
  versionSimpleID: string;
  versionID: string;
  name: string;
  explained: string;
  htmlContent: string;
  plainContent: string;
  variableFields: string[];
  createdAt: string;
  thumbnailUrl?: string;
  testData?: Record<string, string>;
  htmlContentWithTestData?: string;
}

export interface TemplateEditResponse {
  adjustments: Adjustment[];
  changes: Changes;
  progressId: string;
}

export interface Updates {
  versionID: string;
  updatedHtml: string;
}

export interface Changes {
  newVersions: Version[];
  updates: Updates[];
}

export interface Adjustment {
  message: string;
  type: string; // "new" version or "change" version
  designID: string;
  designVersionID: string;
  agentID: string;
  createdAt: string;
  redirectVersionID?: string;
}

export interface SendgridDesign {
  versions: Version[];
  templateID: string;
  designSimpleID: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  editor: string;
  adjustments: Adjustment[];
  activeVersionID?: string;
}

// Helper function to get the active version of a design (falls back to latest)
export const getLatestVersion = (design: SendgridDesign): Version | null => {
  if (!design.versions || design.versions.length === 0) {
    return null;
  }
  if (design.activeVersionID) {
    const active = design.versions.find(
      (v) =>
        v.versionID === design.activeVersionID ||
        v.versionSimpleID === design.activeVersionID
    );
    if (active) return active;
  }
  return design.versions[design.versions.length - 1];
};

export const listTemplates = async (
  projectID: string
): Promise<SendgridDesign[] | null> => {
  const userStore = useUserStore();
  const idToken = userStore.idToken;

  try {
    console.log(`[listTemplates] GET /v2/project/${projectID} start...`);
    const headers: Record<string, string> = {};
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    // Use includeCustomers=false since we only need templates, not customers/messages
    // Use includeTemplateHTML=true since we need full HTML for preview/edit/download
    const resp = await api.get(
      `/v2/project/${projectID}?includeCustomers=false&includeTemplateHTML=true`,
      { headers }
    );

    console.log('[listTemplates] response status:', resp.status);
    const tenant = resp.data; // The full Tenant object
    console.log('[listTemplates] tenant object:', tenant);

    return tenant?.sendgridDesigns ?? [];
  } catch (error) {
    console.error(
      '[listTemplates] Caught error from GET /v2/project/:projectID:',
      error
    );

    if (axios.isAxiosError(error)) {
      console.error(
        '[listTemplates] Axios error status:',
        error.response?.status
      );
      console.error('[listTemplates] Axios error data:', error.response?.data);
    }

    handleAxiosError(error);
    return null;
  }
};

// Add a new template
export const addTemplate = async (
  projectID: string,
  newDesign: SendgridDesign
): Promise<boolean> => {
  const userStore = useUserStore();
  const idToken = userStore.idToken;

  try {
    // Get the Tenant (without customers/messages since we only need templates)
    const getResp = await api.get(
      `/v2/project/${projectID}?includeCustomers=false`,
      {
        headers: { Authorization: `Bearer ${idToken}` }
      }
    );
    const tenant = getResp.data;

    // Push new design into tenant.sendgridDesigns
    tenant.sendgridDesigns.push(newDesign);

    // PUT the entire Tenant back to update
    await api.put(`/v2/project/${projectID}`, tenant, {
      headers: { Authorization: `Bearer ${idToken}` }
    });

    return true;
  } catch (error) {
    handleAxiosError(error);
    return false;
  }
};

// Update an existing design by ID
export const updateTemplate = async (
  projectID: string,
  templateID: string,
  updatedFields: Partial<SendgridDesign>
): Promise<boolean> => {
  const userStore = useUserStore();
  const idToken = userStore.idToken;

  try {
    // Get Tenant (without customers/messages since we only need templates)
    const { data: tenant } = await api.get(
      `/v2/project/${projectID}?includeCustomers=false`,
      {
        headers: { Authorization: `Bearer ${idToken}` }
      }
    );

    // Find design by ID and update
    const idx = tenant.sendgridDesigns.findIndex(
      (d: SendgridDesign) => d.designSimpleID === templateID
    );
    if (idx === -1) {
      console.error('Template not found');
      return false;
    }

    tenant.sendgridDesigns[idx] = {
      ...tenant.sendgridDesigns[idx],
      ...updatedFields
    };

    // PUT updated Tenant
    await api.put(`/v2/project/${projectID}`, tenant, {
      headers: { Authorization: `Bearer ${idToken}` }
    });
    return true;
  } catch (error) {
    handleAxiosError(error);
    return false;
  }
};

// Add a new version to a design by copying an existing version
export const addVersionToTemplate = async (
  projectID: string,
  designSimpleID: string,
  sourceVersionID: string
): Promise<Version | null> => {
  const userStore = useUserStore();
  const idToken = userStore.idToken;
  try {
    const { data } = await api.post(
      `/v2/project/${projectID}/template/add-version`,
      {
        designSimpleID,
        sourceVersionID
      },
      {
        headers: { Authorization: `Bearer ${idToken}` }
      }
    );

    if (data.success && data.version) {
      return data.version as Version;
    }
    console.error('Failed to add version:', data);
    return null;
  } catch (error) {
    handleAxiosError(error);
    return null;
  }
};

// Response shape for fill-variables endpoint
export interface FillVariablesResponse {
  templateId: string;
  versionId: string;
  newVersion?: Version; // Present only if createNewVersion was true
  updatedVersion?: Version; // Present when filling in-place
  filledKeys: string[];
  message: string;
  added?: number;
  alreadyComplete?: boolean;
  status?: string;
  newKeys?: string[];
}

/**
 * Calls backend to fill missing variable placeholders for a template version.
 * Always requests a new version so we can receive the populated content without mutating the original.
 */
export const fillTemplateVariables = async (
  projectID: string,
  templateId: string,
  versionId: string,
  createNewVersion = true
): Promise<FillVariablesResponse | null> => {
  const userStore = useUserStore();
  const idToken = userStore.idToken;
  try {
    const resp = await api.post(
      `/v2/project/${projectID}/template/fill-variables`,
      { templateId, versionId, createNewVersion },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      }
    );
    // Backend uses standard envelope: { success, code, message, data: {...} }
    if (resp.status === 200 && resp.data?.data) {
      const r = resp.data.data;
      return {
        templateId: r.templateId || templateId,
        versionId: r.versionId || versionId,
        newVersion: r.newVersion,
        updatedVersion: r.updatedVersion,
        filledKeys: r.filledKeys || [],
        newKeys: r.newKeys || [],
        message: r.message || resp.data?.message || '',
        added: r.added,
        alreadyComplete: r.alreadyComplete,
        status: r.status
      };
    } else {
      console.warn(
        'fillTemplateVariables unexpected response shape',
        resp.status,
        resp.data
      );
    }
    return null;
  } catch (e) {
    console.error('fillTemplateVariables error', e);
    return null;
  }
};

// Update a specific version of a design
export const updateVersionInTemplate = async (
  projectID: string,
  templateSimpleID: string,
  versionID: string,
  updatedFields: Partial<Version>
): Promise<Version | null> => {
  const userStore = useUserStore();
  const idToken = userStore.idToken;

  try {
    // Get Tenant (without customers/messages since we only need templates)
    const { data: tenant } = await api.get(
      `/v2/project/${projectID}?includeCustomers=false`,
      {
        headers: { Authorization: `Bearer ${idToken}` }
      }
    );

    // Find design by ID
    console.log('Template ID:', templateSimpleID);
    console.log('Version ID:', versionID);
    console.log('Tenant:', tenant.sendgridDesigns);
    console.log('Tenant:', tenant);
    console.log('isequal:', tenant.sendgridDesigns[0].designSimpleID);
    console.log('isequal:', templateSimpleID);
    console.log(tenant.sendgridDesigns[0].designSimpleID === templateSimpleID);
    const designIdx = tenant.sendgridDesigns.findIndex(
      (d: SendgridDesign) => d.designSimpleID === templateSimpleID
    );

    console.log('Design index:', designIdx);

    if (designIdx === -1) {
      console.error('Template not found');
      return null;
    }

    // Find and update the version
    const versionIdx = tenant.sendgridDesigns[designIdx].versions.findIndex(
      (v: Version) => v.versionID === versionID
    );
    if (versionIdx === -1) {
      console.error('Version not found');
      return null;
    }

    tenant.sendgridDesigns[designIdx].versions[versionIdx] = {
      ...tenant.sendgridDesigns[designIdx].versions[versionIdx],
      ...updatedFields
    };

    const version = tenant.sendgridDesigns[designIdx].versions[versionIdx];

    version.createdAt = new Date().toISOString();

    const body = {
      SendgridDesignVersion: {
        templateSimpleID: templateSimpleID,
        versionSimpleID: version.versionSimpleID,
        updatedVersion: version
      }
    };

    // PUT updated Tenant
    const { data: updatedTenant } = await api.put(
      `/v2/project/${projectID}`,
      body,
      {
        headers: { Authorization: `Bearer ${idToken}` }
      }
    );
    const updatedDesignIndex = updatedTenant.sendgridDesigns.findIndex(
      (d: SendgridDesign) => d.designSimpleID === templateSimpleID
    );
    if (updatedDesignIndex === -1) {
      return null;
    }
    const updatedVersionIndex = updatedTenant.sendgridDesigns[
      updatedDesignIndex
    ].versions.findIndex((v: Version) => v.versionID === versionID);
    if (updatedVersionIndex === -1) {
      return null;
    }
    return updatedTenant.sendgridDesigns[updatedDesignIndex].versions[
      updatedVersionIndex
    ];
  } catch (error) {
    handleAxiosError(error);
    return null;
  }
};

export const deleteTemplate = async (
  projectID: string,
  templateID: string
): Promise<boolean> => {
  const userStore = useUserStore();
  const idToken = userStore.idToken;

  console.log('Deleting template:', templateID, 'from project:', projectID);

  try {
    await api.put(
      `v2/project/${projectID}`,
      { deleteTemplate: { templateID } },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return true;
  } catch (error) {
    handleAxiosError(error);
    return false;
  }
};

// Delete a specific version from a design
export const deleteVersionFromTemplate = async (
  projectID: string,
  templateID: string,
  versionID: string
): Promise<boolean> => {
  const userStore = useUserStore();
  const idToken = userStore.idToken;

  try {
    // Get Tenant (without customers/messages since we only need templates)
    const { data: tenant } = await api.get(
      `/v2/project/${projectID}?includeCustomers=false`,
      {
        headers: { Authorization: `Bearer ${idToken}` }
      }
    );

    // Find design by ID
    const designIdx = tenant.sendgridDesigns.findIndex(
      (d: SendgridDesign) => d.designSimpleID === templateID
    );
    if (designIdx === -1) {
      console.error('Template not found');
      return false;
    }

    // Don't allow deleting the last version
    if (tenant.sendgridDesigns[designIdx].versions.length <= 1) {
      console.error('Cannot delete the only version of a template');
      return false;
    }

    // Remove the version
    tenant.sendgridDesigns[designIdx].versions = tenant.sendgridDesigns[
      designIdx
    ].versions.filter((v: Version) => v.versionID !== versionID);
    tenant.sendgridDesigns[designIdx].updatedAt = new Date().toISOString();

    // PUT updated Tenant
    await api.put(`/v2/project/${projectID}`, tenant, {
      headers: { Authorization: `Bearer ${idToken}` }
    });
    return true;
  } catch (error) {
    handleAxiosError(error);
    return false;
  }
};

export const setActiveVersion = async (
  projectID: string,
  templateID: string,
  versionID: string
): Promise<boolean> => {
  const userStore = useUserStore();
  const idToken = userStore.idToken;

  try {
    await api.patch(
      `/v2/project/${projectID}/template/${templateID}/active-version`,
      { versionID },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return true;
  } catch (error) {
    handleAxiosError(error);
    return false;
  }
};

import { API_BASE as baseUrl } from './apiBase';

export async function createEmailTemplates(
  siteUrl: string,
  tenantID: string,
  phone?: string
) {
  const userStore = useUserStore();
  const idToken = userStore.idToken;

  const requestBody = JSON.stringify({
    SiteUrl: siteUrl,
    TenantID: tenantID,
    Phone: phone
  });
  const endpoint = `${baseUrl}/v2/p1-email-gen`;

  // debug logging
  console.log(
    `[createEmailTemplates] POST ${endpoint} with body:`,
    requestBody
  );
  console.log(
    `[createEmailTemplates] Using idToken: ${idToken ? 'present' : 'missing'}`
  );

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`
    },
    body: requestBody
  });

  console.log(`[createEmailTemplates] Response status: ${response.status}`);
  console.log(
    `[createEmailTemplates] Response headers:`,
    Object.fromEntries(response.headers.entries())
  );

  if (!response.ok) {
    const errText = await response.text();
    console.error(
      `[createEmailTemplates] Request failed: ${response.status} - ${errText}`
    );
    throw new Error(`Email generation request failed: ${errText}`);
  }

  // Expect JSON response with the new project SimpleID
  const data = await response.json();

  // detailed response logging
  console.log(`[createEmailTemplates] Response data:`, data);
  console.log(`[createEmailTemplates] Response data type:`, typeof data);
  console.log(
    `[createEmailTemplates] Response data keys:`,
    Object.keys(data || {})
  );

  // Assuming server returns { simpleID: string }
  if (!data.simpleID) {
    console.error(
      `[createEmailTemplates] No simpleID in response. Available keys:`,
      Object.keys(data || {})
    );
    throw new Error('No project ID returned from email generation request.');
  }

  console.log(`[createEmailTemplates] Returning simpleID: ${data.simpleID}`);
  return data.simpleID;
}

/**
 * Sends a template edit request to the AI assistant
 * @param projectID The project ID
 * @param templateID The template ID
 * @param versionID The version ID (if applicable)
 * @param message The user's message/request
 * @param currentHTML The current HTML content of the template
 * @returns An object with the AI's response message and the updated HTML
 */
export const editTemplateWithAI = async (
  projectID: string,
  templateID: string,
  versionID: string,
  message: string
): Promise<TemplateEditResponse | null> => {
  const userStore = useUserStore();
  const idToken = userStore.idToken;

  console.log('Project ID:', projectID);
  console.log('Template ID:', templateID);
  console.log('Version ID:', versionID);

  try {
    const response = await api.post(
      `/v2/project/${projectID}/template/edit`,
      {
        message,
        templateId: templateID,
        versionId: versionID
      },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.status === 200 && response.data && response.data.success) {
      return response.data.data as TemplateEditResponse;
    } else {
      console.error('[editTemplateWithAI] API returned error:', response.data);
      return null;
    }
  } catch (error) {
    console.error('[editTemplateWithAI] API request failed:', error);
    handleAxiosError(error);
    return null;
  }
};

export const createTemplateFromIdea = async (
  projectID: string,
  idea: string
): Promise<SendgridDesign | null> => {
  const userStore = useUserStore();
  const idToken = userStore.idToken;
  try {
    const resp = await api.post(
      `/v2/project/${projectID}/template/create`,
      { Idea: idea },
      {
        headers: { Authorization: `Bearer ${idToken}` }
      }
    );
    // Return the actual design object
    return resp.data?.success && resp.data?.data ? resp.data.data : null;
  } catch (error) {
    handleAxiosError(error);
    return null;
  }
};

// Interface for Stripe verification response
export interface StripeVerificationResponse {
  success: boolean;
  plan: string;
  tenantId: string;
}

// Verify Stripe Checkout Session on backend
export const verifyStripeSession = async (
  sessionId: string
): Promise<StripeVerificationResponse | null> => {
  try {
    const resp = await api.post('/v2/verify-checkout-session', { sessionId });
    return resp.data as StripeVerificationResponse;
  } catch (error) {
    handleAxiosError(error);
    return null;
  }
};

// New API: Improve template via AI
export const improveTemplate = async (
  projectID: string,
  templateId: string,
  versionId: string,
  level = 'enhance'
): Promise<TemplateEditResponse | null> => {
  const userStore = useUserStore();
  const idToken = userStore.idToken;
  try {
    const resp = await api.post(
      `/v2/project/${projectID}/template/improve`,
      { templateId, versionId, level },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      }
    );
    if (resp.status === 200 && resp.data.data) {
      return resp.data.data as TemplateEditResponse;
    }
    console.error('improveTemplate: unexpected response', resp);
    return null;
  } catch (error) {
    handleAxiosError(error);
    return null;
  }
};

// Send a test email for a specific template version
export const sendTemplateTestEmail = async (
  projectID: string,
  designSimpleID: string,
  versionID: string,
  to: string[],
  message?: string
): Promise<boolean> => {
  const userStore = useUserStore();
  const idToken = userStore.idToken;
  try {
    const resp = await api.post(
      `/v2/project/${projectID}/template/send-test`,
      { designSimpleID, versionId: versionID, to, message },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      }
    );
    // Backend responds with { status: 'ok', sent: number, failed: number }
    // Accept both 'ok' (current) and 'queued' (future/legacy) as success markers.
    const data = resp.data;
    return (
      resp.status === 200 &&
      (data?.status === 'ok' || data?.status === 'queued')
    );
  } catch (error) {
    handleAxiosError(error);
    return false;
  }
};

// Get template progress
export const getTemplateProgress = async (
  projectID: string
): Promise<{ progress: TemplateProgress[] } | null> => {
  const userStore = useUserStore();
  const idToken = userStore.idToken;

  try {
    const response = await api.get(
      `/v2/project/${projectID}/template/progress`,
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.status === 200 && response.data.data) {
      return response.data.data as { progress: TemplateProgress[] };
    }
    console.error('getTemplateProgress: unexpected response', response);
    return null;
  } catch (error) {
    console.error('Error fetching template progress:', error);
    return null;
  }
};

export interface TemplateProgress {
  id: string;
  type: 'edit' | 'improve' | 'create';
  templateId: string;
  versionId: string;
  progress: number;
  status: 'pending' | 'processing' | 'completed' | 'error';
  message: string;
  startTime: number;
  endTime?: number;
  canDismiss: boolean;
}

// Refresh template ideas (generates 3 new AI-powered suggestions)
export const refreshTemplateIdeas = async (
  projectID: string
): Promise<string[] | null> => {
  const userStore = useUserStore();
  const idToken = userStore.idToken;

  try {
    const response = await api.post(
      `/v2/project/${projectID}/template-ideas/refresh`,
      {},
      {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      }
    );

    if (response.status === 200 && response.data?.data?.templateIdeas) {
      return response.data.data.templateIdeas as string[];
    }
    return null;
  } catch (error) {
    console.error('Error refreshing template ideas:', error);
    handleAxiosError(error);
    return null;
  }
};
